import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, FileText, Send } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Team Building",
  "Drum Circle",
  "Venue Production",
  "Venue Finding",
  "Artist Management",
  "Band Management",
  "Wedding Decor",
  "Other",
];

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  event_type: "Team Building",
  event_date: "",
  message: "",
};

const inputCls =
  "w-full bg-[#121215] border border-[rgba(212,175,55,0.25)] focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-600 transition-colors duration-300";

export default function EnquiryForm() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        email: form.email || null,
        event_date: form.event_date || null,
        message: form.message || null,
      });
      toast.success("Enquiry received. We'll call you back within a day.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(
        err.response?.data?.detail?.[0]?.msg ||
          err.response?.data?.detail ||
          "Something went wrong. Please call us directly."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="relative py-28 sm:py-36 px-6 lg:px-10 bg-[#0d0d10] border-t border-[rgba(212,175,55,0.15)]"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <span
            data-testid="enquiry-eyebrow"
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#d4af37] font-semibold mb-4 block"
          >
            Begin the Conversation
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-white leading-tight mb-8">
            Let's make your next event <span className="gold-text">legendary</span>
          </h2>
          <p className="text-base text-zinc-400 leading-relaxed max-w-md mb-12">
            Tell us what you're dreaming of — a drum circle for 500, a band for
            your sangeet, or a venue that doesn't exist yet. We'll build it.
          </p>

          <div className="flex flex-col gap-6">
            <a
              data-testid="contact-phone-link"
              href="tel:+919741106724"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 border border-[rgba(212,175,55,0.4)] flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-[#09090b] transition-colors duration-300">
                <Phone size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">
                  Call Us
                </span>
                <span className="text-white group-hover:text-[#d4af37] transition-colors">
                  +91 97411 06724
                </span>
              </span>
            </a>
            <a
              data-testid="contact-phone-link-2"
              href="tel:+917892272280"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 border border-[rgba(212,175,55,0.4)] flex items-center justify-center text-[#d4af37] group-hover:bg-[#d4af37] group-hover:text-[#09090b] transition-colors duration-300">
                <Phone size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">
                  Alternate
                </span>
                <span className="text-white group-hover:text-[#d4af37] transition-colors">
                  +91 78922 72280
                </span>
              </span>
            </a>
            <div className="flex items-center gap-4" data-testid="contact-address">
              <span className="w-11 h-11 border border-[rgba(212,175,55,0.4)] flex items-center justify-center text-[#d4af37]">
                <MapPin size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">
                  Studio
                </span>
                <span className="text-white">Horamavu, Bengaluru — 560043</span>
              </span>
            </div>
            <div className="flex items-center gap-4" data-testid="contact-gst">
              <span className="w-11 h-11 border border-[rgba(212,175,55,0.4)] flex items-center justify-center text-[#d4af37]">
                <FileText size={18} />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-[0.25em] text-zinc-500">
                  GSTIN
                </span>
                <span className="text-white tracking-wider">29ANXPG9863F2Z3</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={1}>
          <form
            data-testid="enquiry-form"
            onSubmit={submit}
            className="bg-[#09090b] border border-[rgba(212,175,55,0.2)] gold-glow p-8 sm:p-10 flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input
                data-testid="enquiry-name-input"
                className={inputCls}
                placeholder="Your name *"
                value={form.name}
                onChange={set("name")}
                maxLength={100}
              />
              <input
                data-testid="enquiry-phone-input"
                className={inputCls}
                placeholder="Phone number *"
                value={form.phone}
                onChange={set("phone")}
                maxLength={20}
              />
            </div>
            <input
              data-testid="enquiry-email-input"
              type="email"
              className={inputCls}
              placeholder="Email (optional)"
              value={form.email}
              onChange={set("email")}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <select
                data-testid="enquiry-type-select"
                className={`${inputCls} appearance-none`}
                value={form.event_type}
                onChange={set("event_type")}
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#121215]">
                    {t}
                  </option>
                ))}
              </select>
              <input
                data-testid="enquiry-date-input"
                type="date"
                className={`${inputCls} [color-scheme:dark]`}
                value={form.event_date}
                onChange={set("event_date")}
              />
            </div>
            <textarea
              data-testid="enquiry-message-input"
              rows={4}
              className={`${inputCls} resize-none`}
              placeholder="Tell us about your event — date, crowd size, vibe..."
              value={form.message}
              onChange={set("message")}
              maxLength={2000}
            />
            <button
              data-testid="enquiry-submit-button"
              type="submit"
              disabled={sending}
              className="group relative mt-2 px-10 py-4 bg-[#d4af37] text-[#09090b] text-xs uppercase tracking-[0.25em] font-semibold overflow-hidden disabled:opacity-60"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                {sending ? "Sending..." : "Send Enquiry"}
                <Send size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </span>
              <span className="absolute inset-0 bg-[#f3e5ab] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
